// 权限检查工具函数

/**
 * 获取当前登录的管理员对象
 * @param {Vuex.Store} store
 * @returns {Object|null}
 */
export function getCurrentAdmin(store) {
  const user = store.state.user
  if (!user) return null
  if (user.role === 'super_admin') {
    return store.state.admins.admins.find(a => a.role === 'super_admin' && a.status === 1) || null
  }
  if (user.role === 'admin') {
    return store.state.admins.admins.find(a => a.email === user.email && a.status === 1) || null
  }
  return null
}

/**
 * 检查当前用户是否有某个权限
 * @param {Vuex.Store} store
 * @param {string} permission
 * @returns {boolean}
 */
export function hasPermission(store, permission) {
  const user = store.state.user
  if (!user) return false
  // 主管角色没有后台权限
  if (user.role === 'manager') return false
  // 超级管理员拥有所有权限
  if (user.role === 'super_admin') return true
  // 普通管理员：只要有分配的任务，默认拥有全部功能权限
  // 不再通过 permissions 数组做细粒度控制，唯一隔离维度是 task_ids
  const admin = store.state.admins.admins.find(a => a.email === user.email)
  if (!admin || admin.status !== 1) return false
  if (!admin.task_ids || admin.task_ids.length === 0) return false
  return true
}

/**
 * 检查是否有任一权限
 * @param {Vuex.Store} store
 * @param {string[]} permissions
 * @returns {boolean}
 */
export function hasAnyPermission(store, permissions) {
  return permissions.some(p => hasPermission(store, p))
}

/**
 * 检查是否拥有所有权限
 * @param {Vuex.Store} store
 * @param {string[]} permissions
 * @returns {boolean}
 */
export function hasAllPermissions(store, permissions) {
  return permissions.every(p => hasPermission(store, p))
}

/**
 * 检查菜单权限（是否有权访问某个路由）
 * @param {Vuex.Store} store
 * @param {string} routePermission
 * @returns {boolean}
 */
export function hasMenuPermission(store, routePermission) {
  return hasPermission(store, routePermission)
}

/**
 * 获取当前用户有权限的菜单列表
 * @param {Vuex.Store} store
 * @param {Array} menuTree
 * @returns {Array}
 */
export function getVisibleMenus(store, menuTree) {
  return menuTree
    .map(group => {
      const visibleChildren = (group.children || []).filter(
        item => hasMenuPermission(store, item.permission)
      )
      if (visibleChildren.length === 0) return null
      return { ...group, children: visibleChildren }
    })
    .filter(Boolean)
}

/**
 * 根据数据范围过滤列表
 * @param {Vuex.Store} store
 * @param {Array} list
 * @param {Object} options
 * @returns {Array}
 */
export function filterByDataScope(store, list, options = {}) {
  const user = store.state.user
  if (!user) return list

  // 超级管理员不过滤
  if (user.role === 'super_admin') return list

  const admin = store.state.admins.admins.find(a => a.email === user.email)
  if (!admin || admin.status !== 1) return list

  const dataScope = admin.data_scope
  const scopeDepartments = admin.scope_departments
  const email = admin.email
  if (dataScope === 'all') return list

  const {
    deptField = 'dept_name',
    managerField = 'manager_email',
    employeeField = null,
    nameField = null
  } = options

  const allEmployees = [
    ...(store.state.employees ? store.state.employees.employees || [] : []),
    ...(store.state.employees ? store.state.employees.resignedEmployees || [] : [])
  ]

  function findEmployee(item) {
    if (employeeField && item[employeeField]) {
      return allEmployees.find(e => e.employee_id === item[employeeField])
    }
    if (nameField && item[nameField]) {
      return allEmployees.find(e =>
        e.real_name === item[nameField] || e.employee_name === item[nameField]
      )
    }
    return null
  }

  if (dataScope === 'departments' && scopeDepartments && scopeDepartments.length > 0) {
    return list.filter(item => {
      if (item[deptField]) {
        return scopeDepartments.includes(item[deptField])
      }
      const emp = findEmployee(item)
      return emp && scopeDepartments.includes(emp.dept_name)
    })
  }

  if (dataScope === 'subordinates') {
    return list.filter(item => {
      if (item[managerField]) {
        return item[managerField] === email
      }
      const emp = findEmployee(item)
      return emp && emp.manager_email === email
    })
  }

  return list
}

/**
 * 获取当前用户的数据范围配置
 * @param {Vuex.Store} store
 * @returns {Object}
 */
export function getDataScope(store) {
  const user = store.state.user
  if (!user || user.role === 'super_admin') {
    return { dataScope: 'all', scopeDepartments: [], email: user ? user.email : '' }
  }
  const admin = store.state.admins.admins.find(a => a.email === user.email)
  if (!admin) {
    return { dataScope: 'all', scopeDepartments: [], email: user.email }
  }
  return {
    dataScope: admin.data_scope,
    scopeDepartments: admin.scope_departments || [],
    email: admin.email
  }
}
